import { useCallback } from 'react';
import { atom, useRecoilState } from 'recoil';
import { localStorageEffect } from 'libs/recoil';
import { useReleaseFlagsLoading } from 'hooks/useReleaseFlags';
import { isIframe } from 'shared/utils';
import { getDeviceInfo } from './localStorageTyped';

type DownloadAppBannerState = {
  lastShownTimestamp: number;
};

type UseDownloadAppBannerState = {
  showBanner: boolean;
  setBannerShown: () => void;
};

const FOURTEEN_DAYS_IN_MS = 1000 * 60 * 60 * 24 * 14;

const DEFAULT_STATE: DownloadAppBannerState = {
  lastShownTimestamp: 0,
};

export const downloadAppBannerStateAtom = atom<DownloadAppBannerState>({
  key: 'DOWNLOAD_APP_BANNER_STATE',
  default: DEFAULT_STATE,
  effects: [localStorageEffect('DOWNLOAD_APP_BANNER_STATE')],
});

const shouldShowBanner = (lastShownTimestamp: number): boolean => {
  const deviceInfo = getDeviceInfo();

  if (!(deviceInfo?.isWeb && deviceInfo?.isMobilePlatform)) {
    return false;
  }

  if (isIframe()) {
    return false;
  }

  const nowMs = Date.now();

  return nowMs - lastShownTimestamp > FOURTEEN_DAYS_IN_MS;
};

export const useDownloadAppBannerState = (): UseDownloadAppBannerState => {
  const [downloadAppBannerState, setDownloadAppBannerState] = useRecoilState(
    downloadAppBannerStateAtom,
  );
  const flagsLoading = useReleaseFlagsLoading();

  const setBannerShown = useCallback(() => {
    setDownloadAppBannerState({ lastShownTimestamp: Date.now() });
  }, [setDownloadAppBannerState]);

  const showBanner = shouldShowBanner(
    downloadAppBannerState.lastShownTimestamp,
  );

  return { showBanner: showBanner && !flagsLoading, setBannerShown };
};
